import { inject, Injectable } from '@angular/core';
import { generateUid } from '@gametailors/web-core/misc';
import { RecommendationApiService } from '../../../services';
import {
  LpActivity,
  LpRidLeeslijstAssignment,
  LpRidLeeslijstConfig,
  LpRidMechanicConfig,
  LpRidWisselklankenAssignment,
  LpRidWisselklankenConfig,
  LpRIDWoord,
  LpSimpleActivityChange,
} from '../../../typings/schema';
import { GeneratedContentDeserializer } from '../../services/recommendations/generated-content-deserializer';
import { MechanicContentGenerator } from '../../services/recommendations/mechanic-content-generator';
import { leeslijstGenerators, wisselklankenGenerators } from '../../services/recommendations/mechanic-generators';
import { generateOfflineLeeslijstAssignment, generateOfflineWisselklankenAssignment } from '../offline-assignment-content';

function returnTo(nextActivity: LpActivity): LpSimpleActivityChange {
  return {
    uid: generateUid(),
    dataType: 'SimpleActivityChange',
    nextActivity,
    simpleTransition: { uid: generateUid(), dataType: 'FadeTransition' },
  };
}

/**
 * Turns a homework assignment's mechanic config into the activity that plays it out. The content comes from the
 * mechanic's generators on the backend, the same ones the dashboard uses, or is chosen locally where no app provides
 * a {@link RecommendationApiService} (a game played without a backend).
 */
@Injectable()
export class RidAssignmentActivityService {
  private readonly deserializer = inject(GeneratedContentDeserializer);
  private readonly recommendationApi = inject(RecommendationApiService, { optional: true });

  // The player is logged in, so the generators run for them.
  private readonly generator = this.recommendationApi
    ? new MechanicContentGenerator(
        (generatorId, inputs) => this.recommendationApi!.generate(generatorId, inputs),
        this.deserializer,
        'homework',
      )
    : undefined;

  /** Throws when there's no content for the config, or the mechanic can't be played as homework yet. */
  public async createActivity(config: LpRidMechanicConfig, nextActivity: LpActivity): Promise<LpActivity> {
    switch (config.dataType) {
      case 'RidWisselklankenConfig':
        return {
          uid: generateUid(),
          dataType: 'RidWisselklankenActivity',
          name: 'Wisselklanken',
          content: await this.generateWisselklankenAssignment(config),
          activityChange: returnTo(nextActivity),
        };
      case 'RidLeeslijstConfig':
        return {
          uid: generateUid(),
          dataType: 'RidLeeslijstActivity',
          name: 'Leeslijst',
          content: await this.generateLeeslijstAssignment(config),
          activityChange: returnTo(nextActivity),
        };
      case 'RidAlgoritmeConfig':
        return { uid: generateUid(), dataType: 'RidAlgoritmeActivity', name: 'Algoritme', activityChange: returnTo(nextActivity) };
      default:
        throw new Error(`Starting an assignment for mechanic '${config.dataType}' is not supported yet`);
    }
  }

  private async generateWisselklankenAssignment(config: LpRidWisselklankenConfig): Promise<LpRidWisselklankenAssignment> {
    if (!this.generator) {
      return generateOfflineWisselklankenAssignment(config, await this.getWords());
    }
    return (await this.generator.generateAssignment(wisselklankenGenerators, config)).unwrap();
  }

  private async generateLeeslijstAssignment(config: LpRidLeeslijstConfig): Promise<LpRidLeeslijstAssignment> {
    if (!this.generator) {
      return generateOfflineLeeslijstAssignment(config, await this.getWords());
    }
    return (await this.generator.generateAssignment(leeslijstGenerators, config)).unwrap();
  }

  private getWords(): Promise<LpRIDWoord[]> {
    return this.deserializer.getAll<LpRIDWoord>('RIDWoord');
  }
}
